import React, { useState, useEffect, useRef, createRef } from "react";
import { userService } from "../../_services";
import { Navigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import domtoimage from 'dom-to-image';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Download, Printer, Maximize2 } from "lucide-react";
import CardFront from "../Summary/CardFront";
import CardBack from "../Summary/CardBack";
import FlyerPage1 from "./flyer1";
import FlyerPage2 from "./flyer2";
import pdf1 from '../../resources/pricing-sheets/NuLifeSpan.Top.50.Walgreens.Mar25.pdf';
import pdf2 from '../../resources/pricing-sheets/NuLifeSpan.Top.CVS.Mar.25.pdf';
import pdf3 from '../../resources/pricing-sheets/NuLifeSpan.Walmart.top.Mar.25.pdf';

/**
 * Order Cards Page
 * Modernized with shadcn components - Order cards and download flyers/pricing sheets
 */
function OrderCardsPage() {
    // State
    const [groupNumbers, setGroupNumbers] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [groupStatus, setGroupStatus] = useState('');
    const [groupData, setGroupData] = useState(null);
    const [isPrimary, setIsPrimary] = useState('');
    const [cardsData, setCardsData] = useState([]);
    const [zoomLeft, setZoomLeft] = useState(false);
    const [zoomRight, setZoomRight] = useState(false);
    const [pricingSheetData, setPricingSheetData] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);

    // Refs
    const iframeRefs = {
        pdf1: createRef(),
        pdf2: createRef(),
        pdf3: createRef(),
    };

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');
        
        getUserGroups(userId, userName);
        getPricingSheets(userId);

        // Event listeners for zoom modals
        document.addEventListener('click', handleOutsideClick);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const getUserGroups = async (userId, userName) => {
        try {
            const apidata = await userService.GetUserGroups(userId, userName);
            if (apidata && apidata.length > 0) {
                setGroupNumbers(apidata);
                setSelectedGroup(apidata[0].groupnumber);
                setGroupStatus(apidata[0].status);
                setIsPrimary(apidata[0].isPrimary);
                setGroupData(apidata[0]);
                
                // Generate cards data after groups are loaded
                const cards = getCardsDataForGroup(apidata[0].status);
                setCardsData(cards);
            }
        } catch (error) {
            console.error('Error fetching user groups:', error);
        }
    };

    const getPricingSheets = async (userId) => {
        try {
            const apiData = await userService.FetchPricingSheets(userId);
            if (apiData && apiData.length > 0) {
                setPricingSheetData(apiData);
            }
        } catch (error) {
            console.error('Error fetching pricing sheets:', error);
        }
    };

    const getCardsDataForGroup = (status) => {
        const userRole = window.atob(localStorage.getItem('UserRole'));
        let cards = [{
            cardname: 'Glic Card',
            classnames: 'glic_card',
            bin: '023518',
            pcn: 'ARX',
        }];

        if (status === 'Yes') {
            cards.push(
                {
                    cardname: 'Citizen Card',
                    classnames: 'citizen_card',
                    bin: '015284',
                    pcn: 'CRX'
                },
                {
                    cardname: 'NVT Card',
                    classnames: 'nvt_card',
                    bin: '610602',
                    pcn: 'NVT'
                },
                {
                    cardname: 'CVS Card',
                    classnames: 'cvs_card',
                    bin: '610398',
                    pcn: 'CV1'
                },
                {
                    cardname: 'DST Card',
                    classnames: 'dst_card',
                    bin: '600428',
                    pcn: 'HRX'
                }
            );
        }

        return cards.map((card) => {
            let datetime = Math.floor(Math.random() * 1313131);
            let mId = (card.classnames === 'cvs_card' || card.classnames === 'walmart_card')
                ? `AX${datetime}`
                : userRole === 'consumer'
                    ? '100-4542'
                    : `AX${datetime}`;

            return { ...card, mId };
        });
    };

    const handleGroupChange = (value) => {
        setSelectedGroup(value);
        const selected = groupNumbers.find(g => g.groupnumber === value);
        if (selected) {
            setGroupData(selected);
            setIsPrimary(selected.isPrimary);
            setGroupStatus(selected.status);
            const cards = getCardsDataForGroup(selected.status);
            setCardsData(cards);
        }
    };

    const handleOutsideClick = (event) => {
        if (event.target.classList.contains("zoomed-flyer")) {
            setZoomLeft(false);
            setZoomRight(false);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            setZoomLeft(false);
            setZoomRight(false);
        }
    };

    const printAll = () => {
        setIsPrinting(true);
        const printableElements = document.querySelector('.all_cards');
        if (printableElements) {
            const printHtml = printableElements.outerHTML;
            const originalHtml = document.body.innerHTML;
            document.body.innerHTML = printHtml;
            window.print();
            document.body.innerHTML = originalHtml;
            window.location.reload();
        }
    };

    const downloadAll = async () => {
        setIsDownloading(true);
        const ids = [];
        document.querySelectorAll('.cards').forEach((el) => {
            if (el.id) ids.push(el.id);
        });
        ids.push('flyer1', 'flyer2');

        try {
            for (const id of ids) {
                await downloadElement(id, id);
            }
            
            // Download pricing sheets
            [pdf1, pdf2, pdf3].forEach((pdfUrl, index) => {
                setTimeout(() => {
                    handleDownload(pdfUrl);
                }, index * 500);
            });
        } catch (error) {
            console.error('Error downloading:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const downloadElement = (id, name) => {
        return domtoimage.toPng(document.getElementById(id))
            .then((blob) => {
                return new Promise((resolve) => {
                    const pdf = new jsPDF("p", "mm", "a4");
                    const pdfWidth = pdf.internal.pageSize.getWidth();
                    const pdfHeight = pdf.internal.pageSize.getHeight();
                    const img = new Image();
                    img.src = blob;

                    img.onload = function () {
                        const imgWidth = img.width;
                        const imgHeight = img.height;
                        const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
                        const scaledWidth = imgWidth * ratio;
                        const scaledHeight = imgHeight * ratio;
                        const xOffset = (pdfWidth - scaledWidth) / 2;
                        const yOffset = (pdfHeight - scaledHeight) / 2;

                        pdf.addImage(blob, 'JPEG', xOffset, yOffset, scaledWidth, scaledHeight);
                        pdf.save(name + '.pdf');
                        resolve();
                    };
                });
            });
    };

    const printIndividual = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const printHtml = element.outerHTML;
            const originalHtml = document.body.innerHTML;
            document.body.innerHTML = printHtml;
            setTimeout(() => {
                window.print();
                document.body.innerHTML = originalHtml;
                window.location.reload();
            }, 300);
        }
    };

    const downloadIndividual = async (id, name) => {
        try {
            await downloadElement(id, name);
        } catch (error) {
            console.error('Error downloading individual:', error);
        }
    };

    const handleDownload = (pdfUrl) => {
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = pdfUrl.split("/").pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePrint = (pdfKey) => {
        if (iframeRefs[pdfKey]?.current) {
            iframeRefs[pdfKey].current.contentWindow.focus();
            iframeRefs[pdfKey].current.contentWindow.print();
        }
    };

    // Auth check
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate to="/signup" replace />;
    }

    localStorage.setItem('pagename', 'ordercards');

    return (
        <div className="w-full space-y-6">
            {/* Heading */}
            <h1 className="text-2xl font-semibold text-brand-dark">
                Order Cards & Marketing Materials
            </h1>

            {/* Controls - Group Selection and Action Buttons */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                {/* Group Selection */}
                {groupNumbers.length > 0 && (
                    <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-brand-dark">
                            Select Group Number:
                        </label>
                        <Select value={selectedGroup} onValueChange={handleGroupChange}>
                            <SelectTrigger className="w-64">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {groupNumbers.map((group, index) => (
                                    <SelectItem key={index} value={group.groupnumber}>
                                        {group.groupnumber}
                                        {group.isPrimary === 'True' && ' (Primary)'}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                    <Button
                        onClick={downloadAll}
                        disabled={isDownloading}
                        variant="secondary"
                    >
                        <Download className="mr-2 h-4 w-4" />
                        {isDownloading ? 'Downloading...' : 'Download All'}
                    </Button>
                    <Button
                        onClick={printAll}
                        variant="outline"
                        disabled={isPrinting}
                    >
                        <Printer className="mr-2 h-4 w-4" />
                        Print All
                    </Button>
                </div>
            </div>

            {/* Cards Section */}
            <div className="all_cards space-y-4">
                {cardsData.map((card, index) => (
                    <Card key={index} id={`card${index + 1}`} className="cards">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-lg font-semibold text-brand-dark">
                                {card.cardname}
                            </CardTitle>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => downloadIndividual(`card${index + 1}`, card.cardname)}
                                >
                                    <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => printIndividual(`card${index + 1}`)}
                                >
                                    <Printer className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <CardFront
                                    cardData={{
                                        bin: card.bin,
                                        pcn: card.pcn,
                                        groupNumber: selectedGroup,
                                        mId: card.mId
                                    }}
                                    customclass={card.classnames}
                                />
                                <CardBack
                                    cardData={{
                                        bin: card.bin,
                                        pcn: card.pcn,
                                        groupNumber: selectedGroup,
                                        mId: card.mId
                                    }}
                                    customclass={card.classnames}
                                />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Flyers Section */}
            <div className="space-y-4">
                <h2 className="text-xl font-semibold text-brand-dark">Printable Flyers</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flyers">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-lg font-semibold text-brand-dark">
                                Flyer 1
                            </CardTitle>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setZoomLeft(true)}
                                >
                                    <Maximize2 className="h-4 w-4" />
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => downloadIndividual('flyer1', 'Flyer1')}
                                >
                                    <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => printIndividual('flyer1')}
                                >
                                    <Printer className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="cursor-pointer" onClick={() => setZoomLeft(true)}>
                                <FlyerPage1 />
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-lg font-semibold text-brand-dark">
                                Flyer 2
                            </CardTitle>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setZoomRight(true)}
                                >
                                    <Maximize2 className="h-4 w-4" />
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => downloadIndividual('flyer2', 'Flyer2')}
                                >
                                    <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => printIndividual('flyer2')}
                                >
                                    <Printer className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="cursor-pointer" onClick={() => setZoomRight(true)}>
                                <FlyerPage2 />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Pricing Sheets Section */}
            <div className="pricing-sheets space-y-4">
                <h2 className="text-xl font-semibold text-brand-dark">Pricing Sheets</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                        { pdf: pdf1, key: 'pdf1', title: 'Walgreens Pricing' },
                        { pdf: pdf2, key: 'pdf2', title: 'CVS Pricing' },
                        { pdf: pdf3, key: 'pdf3', title: 'Walmart Pricing' }
                    ].map((item) => (
                        <Card key={item.key}>
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-base font-semibold text-brand-dark">
                                    {item.title}
                                </CardTitle>
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDownload(item.pdf)}
                                    >
                                        <Download className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handlePrint(item.key)}
                                    >
                                        <Printer className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <iframe
                                    ref={iframeRefs[item.key]}
                                    src={item.pdf}
                                    className="w-full h-64 border rounded"
                                    title={item.title}
                                />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Zoomed Flyers */}
            {zoomLeft && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 zoomed-flyer p-4" onClick={() => setZoomLeft(false)}>
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
                        <FlyerPage1 />
                    </div>
                </div>
            )}

            {zoomRight && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 zoomed-flyer p-4" onClick={() => setZoomRight(false)}>
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
                        <FlyerPage2 />
                    </div>
                </div>
            )}

            {/* Hidden print container */}
            <div className="printFlyers hidden" style={{ display: isDownloading || isPrinting ? 'block' : 'none' }}>
                {/* Print-friendly versions go here */}
            </div>
        </div>
    );
}

export default OrderCardsPage;
